import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../../../../../../../../components/Common/UI/Dialog';
import { cn } from '../../../../../../../../utils/styles';
import { Button } from '../../../../../../../../components/Common/UI/Button';
import Separator from '../../../../../../../../components/Common/UI/Separator';
import { AccountManagementSubRow, IDeliveryType, INotificationType } from '../../../../columns';
import { IDeliveryModal } from '../../index';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../../../components/Common/UI/Select';
import { Input } from '../../../../../../../../components/Common/UI/Input';

interface IModalEditDelivery {
  handleSave: (notifyType: INotificationType, deliveryType: IDeliveryType, type: IDeliveryModal['statement']) => void;
  open: boolean;
  statement: IDeliveryModal['statement'];
  handleOpen: (payload: IDeliveryModal) => void;
  data: AccountManagementSubRow;
}

export const deliveryType: { key: string; label: IDeliveryType }[] = [
  { key: 'Electronic', label: 'Electronic' },
  { key: 'Paper', label: 'Paper' },
  { key: 'Both', label: 'Both' },
];

export const notifyType: { key: string; label: INotificationType }[] = [
  { key: 'Email', label: 'Email' },
  { key: 'SMS', label: 'SMS' },
  { key: 'Email & SMS', label: 'Email & SMS' },
];

interface IState {
  deliveryType: IDeliveryType;
  notifyType: INotificationType;
  email: string;
  phone: string;
}

const initialData: IState = {
  deliveryType: 'Paper',
  notifyType: 'Email',
  email: '',
  phone: '',
};

function ModalEditDelivery({ open, handleOpen, handleSave, statement, data }: IModalEditDelivery) {
  const [state, setState] = useState<IState>(initialData);

  useEffect(() => {
    if (open) {
      setState({
        ...state,
        deliveryType: (data[statement as keyof AccountManagementSubRow] as IDeliveryType) || 'Both',
        email: data.email,
        phone: data.phone,
      });
    }
  }, [open]);

  const handleOnSelect = (payload: IDeliveryType | INotificationType, key: keyof IState) => {
    setState({
      ...state,
      [key]: payload,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={e => {
        handleOpen({ open: e, statement, type: 'edit' });
      }}
    >
      <DialogContent className={cn('max-h-[98%] max-w-[600px]')}>
        <DialogTitle className="w-full h-auto">
          Edit Delivery Preference
          <div className="font-normal mt-[10px] text-[16px]">
            <span className="text-primary-60">Current Delivery Preference:</span>
            <span className="text-primary-80 font-medium ml-[5px]">
              {data[statement as keyof AccountManagementSubRow].length > 1 ? 'Both' : 'Electronic'}
            </span>
          </div>
        </DialogTitle>

        <div className="grid gap-[20px]">
          <Select onValueChange={e => handleOnSelect(e as IDeliveryType, 'deliveryType')} value={state.deliveryType}>
            <SelectTrigger className="w-full" label="Delivery Type">
              <SelectValue placeholder="Delivery Type" />
            </SelectTrigger>
            <SelectContent>
              {deliveryType.map(({ key, label }) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {state.deliveryType === 'Both' || state.deliveryType === 'Electronic' ? (
            <Select onValueChange={e => handleOnSelect(e as INotificationType, 'notifyType')} value={state.notifyType}>
              <SelectTrigger className="w-full" label="Notify Type">
                <SelectValue placeholder="Notify Type" />
              </SelectTrigger>
              <SelectContent>
                {notifyType.map(({ key, label }) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
          {state.notifyType !== 'SMS' && (state.deliveryType === 'Both' || state.deliveryType === 'Electronic') ? (
            <Input
              label="Email Address*"
              placeholder="Email address"
              value={state.email}
              onChange={e => setState({ ...state, email: e.target.value })}
            />
          ) : null}
          {state.notifyType !== 'Email' && (state.deliveryType === 'Both' || state.deliveryType === 'Electronic') ? (
            <Input
              prefix="+1"
              iconPosition="left"
              label="Phone Number*"
              placeholder="Phone number"
              value={state.phone}
              onChange={e => setState({ ...state, phone: e.target.value })}
            />
          ) : null}
        </div>
        <Separator className="m-0" />
        <div className="flex justify-end gap-4">
          <Button
            variant="link"
            className="py-3 hover:no-underline"
            onClick={() => handleOpen({ open: false, statement, type: 'edit' })}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            className="py-3 !bg-addition5 hover:opacity-85 px-[20px]"
            onClick={() => {
              handleSave(state.notifyType, state.deliveryType, statement);
              handleOpen({ open: false, statement, type: 'edit' });
            }}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ModalEditDelivery;
